/**
 * Sayz Find My Size — "Measuring Tape" calculating state.
 *
 * Replaces the empty-while-fetching gap on the result step with a branded
 * micro-moment that mirrors the ResultStep's layout exactly, so swapping it
 * in and out introduces zero CLS. A horizontal SVG measuring tape unrolls
 * left-to-right (stroke-dasharray), the visitor's actual measurements fade
 * in on top of the ticks, and a status line typewrites a sequence of
 * thinking phrases. When the backend recommendation arrives, this component
 * is replaced by ResultStep via a parent-level fade transition.
 *
 * Decisions
 *  • CSS-only animations (no RAF, no JS loop) — performance is free.
 *  • Marks per measurement (Boy / Kilo / Yaş / Bel / Göğüs / Kalça / Omuz)
 *    with the merchant's actual accent color on the active tick.
 *  • Loops gracefully — if the backend is slow the user still sees the
 *    animation re-ignite rather than freezing on a static tape.
 */

import { useMemo } from "react";
import { Sparkles } from "lucide-react";

const BODY_LABELS = {
    height: { label: "Boy", unit: "cm" },
    weight: { label: "Kilo", unit: "kg" },
    age: { label: "Yaş", unit: "" },
    chest: { label: "Göğüs", unit: "" },
    waist: { label: "Bel", unit: "" },
    hip: { label: "Kalça", unit: "" },
    shoulder: { label: "Omuz", unit: "" },
};

// Only numeric fields show up as tick marks on the tape — "fit" and the body
// shape enums are shown separately (or not at all) so the tape stays legible.
const TAPE_ORDER = ["height", "weight", "age", "chest", "waist", "hip"];

export const MeasuringTapeLoader = ({ profile, accent, copy }) => {
    // Pick the measurements the visitor actually gave us so each tick on
    // the tape is a real, personal data point — not a generic placeholder.
    const marks = useMemo(() => {
        return TAPE_ORDER
            .map((k) => {
                const v = profile?.[k];
                if (v === undefined || v === null || v === "") return null;
                const meta = BODY_LABELS[k];
                // Numeric vs. body-shape (string): only show numerics on
                // the tape — body-shape values ("normal","wide",...) read
                // oddly here, we handle those below the tape as text.
                if (typeof v !== "number") return null;
                return { key: k, label: meta.label, value: v, unit: meta.unit };
            })
            .filter(Boolean);
    }, [profile]);

    const resultLabel = copy?.resultLabel || "Önerimiz";
    const loadingText = copy?.resultLoadingText || "Bedenin hesaplanıyor";

    // The tape is a 320px wide viewBox, ticks evenly distributed. We don't
    // scale by `marks.length` so even a profile with a single measurement
    // still gets a well-proportioned tape (ticks collapse toward center).
    const tickCount = Math.max(marks.length, 3);
    const tickPositions = useMemo(
        () => Array.from({ length: tickCount }, (_, i) => 20 + (280 * i) / (tickCount - 1)),
        [tickCount],
    );

    return (
        <div
            className="flex flex-col items-center justify-center text-center space-y-5 min-h-full"
            data-testid="fms-step-result-loading"
        >
            {/* Icon — mirrors ResultStep's circular accent icon so the
                visual anchor at the top doesn't jump when the state swaps. */}
            <span
                className="inline-flex h-11 w-11 items-center justify-center rounded-full sayz-tape-breathe"
                style={{ background: accent, color: "#fff" }}
            >
                <Sparkles className="h-[18px] w-[18px]" strokeWidth={1.75} />
            </span>

            {/* ÖNERİMİZ label — same slot as ResultStep for layout parity. */}
            <span
                className="text-[11px] font-semibold uppercase tracking-wider"
                style={{ color: accent }}
            >
                {resultLabel}
            </span>

            {/* The measuring tape itself. SVG viewBox 0 0 320 80, rendered
                at the max-w-[360px] frame width so it occupies the same
                horizontal space that the 72px result size letter takes
                below. */}
            <div className="w-full max-w-[320px]" aria-hidden="true">
                <svg
                    viewBox="0 0 320 80"
                    width="100%"
                    style={{ display: "block" }}
                    data-testid="fms-measuring-tape"
                >
                    <defs>
                        {/* Soft gloss gradient — darker at the top edge so
                            the tape reads as a real tangible object rather
                            than a flat line. */}
                        <linearGradient id="sayz-tape-grad" x1="0" x2="0" y1="0" y2="1">
                            <stop offset="0%"   stopColor={accent} stopOpacity="0.14" />
                            <stop offset="55%"  stopColor={accent} stopOpacity="0.07" />
                            <stop offset="100%" stopColor={accent} stopOpacity="0.14" />
                        </linearGradient>
                    </defs>

                    {/* Tape body — a 14px tall band with gradient fill. The
                        reveal is driven by clip-path on the group below,
                        not by stroke-dash, so the ticks and body unroll
                        together as one object. */}
                    <g className="sayz-tape-reveal" style={{ transformOrigin: "left center" }}>
                        <rect x="18" y="33" width="284" height="14" rx="2" fill="url(#sayz-tape-grad)" />
                        {/* Centerline — the baseline the ziyaretçi reads
                            measurements off of. */}
                        <line
                            x1="20" y1="40" x2="300" y2="40"
                            stroke={accent}
                            strokeWidth="1"
                            strokeLinecap="round"
                            opacity="0.42"
                        />
                        {/* Tick marks — alternating long/short like a real
                            tape measure. Long ticks are 10px, short 5px. */}
                        {tickPositions.map((x, i) => {
                            const isLong = i % 2 === 0;
                            return (
                                <line
                                    key={`tick-${i}`}
                                    x1={x} y1={40 - (isLong ? 10 : 5)}
                                    x2={x} y2={40 + (isLong ? 10 : 5)}
                                    stroke={accent}
                                    strokeWidth={isLong ? "1.4" : "1"}
                                    strokeLinecap="round"
                                    opacity={isLong ? "0.75" : "0.5"}
                                />
                            );
                        })}

                        {/* Measurement labels under each tick — pop in with
                            staggered delays so the visitor sees their own
                            numbers get "measured" one by one. */}
                        {marks.map((m, i) => {
                            // Center the label under its tick; if fewer
                            // marks than ticks, we align to the first N.
                            const x = tickPositions[i] ?? tickPositions[0];
                            return (
                                <g
                                    key={`label-${m.key}`}
                                    className="sayz-tape-chip"
                                    style={{ animationDelay: `${260 + i * 130}ms` }}
                                >
                                    <text
                                        x={x} y={66}
                                        textAnchor="middle"
                                        fontSize="8"
                                        fontWeight="600"
                                        letterSpacing="0.08em"
                                        fill={accent}
                                        style={{ textTransform: "uppercase" }}
                                    >
                                        {m.label}
                                    </text>
                                    <text
                                        x={x} y={78}
                                        textAnchor="middle"
                                        fontSize="10"
                                        fontWeight="700"
                                        fill="currentColor"
                                        className="text-foreground"
                                    >
                                        {m.value}{m.unit}
                                    </text>
                                </g>
                            );
                        })}
                    </g>
                </svg>
            </div>

            {/* Status copy — editorial typographic hierarchy to match the
                tape aesthetic above: tiny uppercase "ÖLÇÜYOR" eyebrow in
                accent (echoes the "ÖNERİMİZ" slot) + the measuring phrase
                itself in accent color with medium weight + slight tracking.
                The dot ellipsis stays — it carries the forward-motion that
                the user explicitly asked us to preserve — but inherits the
                refined accent treatment so it reads as part of the tape
                family instead of a generic loading indicator. */}
            <div
                className="flex flex-col items-center gap-1.5"
                data-testid="fms-measuring-status"
            >
                <span
                    className="text-[10px] font-semibold uppercase sayz-tape-eyebrow"
                    style={{ color: accent, letterSpacing: "0.22em" }}
                >
                    Ölçüyor
                </span>
                <p
                    className="text-[14px] leading-snug sayz-tape-pulse"
                    style={{ color: accent, letterSpacing: "0.015em", fontWeight: 500 }}
                >
                    <span>{loadingText}</span>
                    <span className="sayz-ellipsis" aria-hidden="true">
                        <span>.</span><span>.</span><span>.</span>
                    </span>
                </p>
            </div>

            {/* Scoped animations — kept in the component so the loader is
                drop-in without any CSS imports. All timings are tuned to
                feel "deliberate" rather than "frantic". */}
            <style>{`
                @keyframes sayz-tape-reveal {
                    0%   { clip-path: inset(0 100% 0 0); opacity: 0.4; }
                    100% { clip-path: inset(0 0 0 0);     opacity: 1; }
                }
                .sayz-tape-reveal {
                    animation: sayz-tape-reveal 820ms cubic-bezier(.22,.9,.3,1) both;
                }
                @keyframes sayz-tape-chip-in {
                    0%   { opacity: 0; transform: translateY(4px); }
                    100% { opacity: 1; transform: translateY(0);    }
                }
                .sayz-tape-chip {
                    opacity: 0;
                    animation: sayz-tape-chip-in 320ms cubic-bezier(.22,.9,.3,1) forwards;
                    transform-box: fill-box;
                    transform-origin: center;
                }
                @keyframes sayz-tape-breathe {
                    0%, 100% { transform: scale(1);    }
                    50%      { transform: scale(1.06); }
                }
                .sayz-tape-breathe {
                    animation: sayz-tape-breathe 1800ms ease-in-out infinite;
                }
                .sayz-ellipsis { display: inline-block; margin-left: 3px; letter-spacing: 0.1em; }
                .sayz-ellipsis > span {
                    display: inline-block;
                    animation: sayz-ellipsis-wave 1200ms ease-in-out infinite;
                    opacity: 0.35;
                }
                .sayz-ellipsis > span:nth-child(2) { animation-delay: 180ms; }
                .sayz-ellipsis > span:nth-child(3) { animation-delay: 360ms; }
                @keyframes sayz-ellipsis-wave {
                    0%, 60%, 100% { opacity: 0.35; transform: translateY(0);    }
                    30%           { opacity: 1;    transform: translateY(-2px); }
                }
                /* Status line soft breathe — ties the text to the same
                   1800ms rhythm as the icon above. Range 0.75–1 so the
                   wording stays legible throughout (unlike a 0→1 pulse
                   which reads as "disappearing"). Kept subtle so it adds
                   life without competing with the ellipsis motion. */
                @keyframes sayz-tape-pulse {
                    0%, 100% { opacity: 0.78; }
                    50%      { opacity: 1;    }
                }
                .sayz-tape-pulse {
                    animation: sayz-tape-pulse 1800ms ease-in-out infinite;
                }
                .sayz-tape-eyebrow {
                    opacity: 0;
                    animation: sayz-tape-chip-in 420ms cubic-bezier(.22,.9,.3,1) 160ms forwards;
                }
            `}</style>
        </div>
    );
};

export default MeasuringTapeLoader;
